import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { Component, inject, Injector } from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxDate } from '@onyx/angular';
import {
  OnyxAutoFocusDirective,
  FormHelper,
  OnyxButtonComponent,
  OnyxDatepickerComponent,
  onyxDateValidator,
  OnyxDropdownComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxUploadComponent,
} from '@onyx/angular';
import { Subject } from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import type { ContractorDocumentType } from '../../../common/enums/contractor-document-type';

interface ContractorAddDocumentsModalData {
  mode: OnyxFormMode.ADD;
  documents: ContractorDocumentsFormGroup;
}

interface ContractorEditDocumentsModalData {
  mode: OnyxFormMode.EDIT;
  documents: ContractorDocumentsFormGroup;
  index: number;
}

export type ContractorDocumentsModalData =
  ContractorAddDocumentsModalData | ContractorEditDocumentsModalData;

export type ContractorDocumentsFormGroup = ReturnType<
  typeof ContractorDocumentsModalComponent.buildForm
>;

@Component({
  selector: 'app-contractor-documents-modal',
  imports: [
    OnyxModalComponent,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxButtonComponent,
    AsyncPipe,
    OnyxDatepickerComponent,
    TranslatePipe,
    OnyxUploadComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './contractor-documents-modal.component.html',
  styleUrl: './contractor-documents-modal.component.scss',
})
export class ContractorDocumentsModalComponent {
  protected readonly data = inject<ContractorDocumentsModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(
    DialogRef<ContractorDocumentsFormGroup>,
  );
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  protected readonly I18N = 'contractors.contractorForm.documents.modal';

  protected readonly OnyxFormMode = OnyxFormMode;

  protected readonly documentTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.CONTRACTOR_DOCUMENT_TYPE,
  );

  protected close$ = new Subject<ContractorDocumentsFormGroup | undefined>();

  protected async submit(): Promise<void> {
    const form = this.data.documents;
    if (!(await ValidationHelper.checkValidity(form, this.injector))) return;

    this.close$.next(form);
  }

  protected cancel(): void {
    FormHelper.reset(this.data.documents);
    this.close$.next(undefined);
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      type: fb.control<ContractorDocumentType | null>(null, [
        Validators.required,
      ]),
      expirationDate: fb.control<OnyxDate | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),
      scan: fb.control<string | null>(null, [Validators.required]),
    });
  }
}
